import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { Button } from '../../../../../../../../components/button/button';
import { toastError } from '../../../../../../../../utils/toast';
import { useMeterCsvExportDownload, useMeterCsvExportOrder } from '../../../../../../api/meter/meter.hooks';

type CsvExportProps = {
  meterId: string;
};

export const CsvExport: React.FC<CsvExportProps> = ({ meterId }) => {
  const { t } = useTranslation('channel-details');
  const { t: tc } = useTranslation('common');
  const [savedExportId, setSavedExportId] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { mutate: csvExportOrder } = useMeterCsvExportOrder();
  const { mutate: csvExportDownload } = useMeterCsvExportDownload();

  const handleCsvExportDownload = (exportId: string) => {
    csvExportDownload(exportId, {
      onSuccess: (downloadResponse) => {
        const data = downloadResponse.data;

        const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${meterId}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        setIsLoading(false);
      },
      onError: (error) => {
        setIsLoading(false);
        console.error('Error downloading CSV:', error);
        toastError({ content: tc('errors.somethingWentWrong') });
      },
    });
  };

  const handleCsvExportOrder = () => {
    setIsLoading(true);
    if (savedExportId) handleCsvExportDownload(savedExportId);

    csvExportOrder(meterId, {
      onSuccess: (response) => {
        const exportId = response.data.exportId;
        setSavedExportId(exportId);

        setTimeout(() => {
          handleCsvExportDownload(exportId);
        }, 800);
      },
      onError: (error) => {
        console.error('Error exporting CSV:', error);
        toastError({ content: tc('errors.somethingWentWrong') });
      },
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <hr className="m-t-24 m-b-24" />
      <Typography color="var(--color-type-secondary)" fontSize={12} marginBottom={1}>
        {t('downloadMeasurementsDataInfo')}
      </Typography>
      <Button
        onClick={handleCsvExportOrder}
        type="accent"
        disabled={isLoading}
        size="small"
        fullWidth={false}
        isLoading={isLoading}
      >
        {t('downloadMeasurementsData')}
      </Button>
    </Box>
  );
};
